import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, XMarkIcon, PhotoIcon, BoltIcon,
  ArrowUpTrayIcon, BanknotesIcon, Cog6ToothIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { prezzoNegozioConIva } from './bike/statiPratica';

const FORM_VUOTO = {
  modello: '', ean: '', id_marca: '', brand: '', nomeProdotto: '',
  prezzoPubblico: '', prezzoNegozio: '', arrivoPrevisto: '', specializzati: '', img: '', schedaTecnicaPdf: ''
};

const formatPrezzo = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function BikeList() {
  const [bici, setBici] = useState([]);
  const [marche, setMarche] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [tipi, setTipi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // filtri
  const [search, setSearch] = useState('');
  const [fMarca, setFMarca] = useState('');
  const [fCategoria, setFCategoria] = useState('');
  const [fTipo, setFTipo] = useState('');
  const [fElettrica, setFElettrica] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState('DESC');
  const [percProvv, setPercProvv] = useState(null);

  // form
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(FORM_VUOTO);
  const [salvando, setSalvando] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [importing, setImporting] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const excelInputRef = useRef(null);

  const caricaLookup = useCallback(async () => {
    try {
      const [m, c, t] = await Promise.all([
        api.get('/bici/marche'), api.get('/bici/categorie'), api.get('/bici/tipi')
      ]);
      setMarche(m.data || []);
      setCategorie(c.data || []);
      setTipi(t.data || []);
    } catch (e) {
      toast.error('Errore caricamento anagrafiche bici');
    }
  }, []);

  const caricaBici = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12 };
      if (search) params.search = search;
      if (fMarca) params.marca_id = fMarca;
      if (fCategoria) params.categoria_id = fCategoria;
      if (fTipo) params.tipo_id = fTipo;
      if (fElettrica) params.elettrica = fElettrica;
      params.sort = sortBy;
      params.order = sortDir;
      const { data } = await api.get('/bici', { params });
      setBici(data.bici || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 0);
    } catch (e) {
      toast.error('Errore caricamento bici');
    } finally {
      setLoading(false);
    }
  }, [page, search, fMarca, fCategoria, fTipo, fElettrica, sortBy, sortDir]);

  useEffect(() => { caricaLookup(); }, [caricaLookup]);
  useEffect(() => { caricaBici(); }, [caricaBici]);
  useEffect(() => { api.get('/bici/impostazioni').then(({ data }) => setPercProvv(Number(data.percentualeProvvigione) || 0)).catch(() => {}); }, []);

  // Provvigione agente per una bici: (pubblico − negozio con IVA 22%) × percentuale
  const provvigioneBici = (b) => {
    if (percProvv == null || b.prezzoPubblico == null || b.prezzoNegozio == null) return null;
    const margine = Number(b.prezzoPubblico) - prezzoNegozioConIva(b.prezzoNegozio);
    return margine * percProvv / 100;
  };

  const apriNuovo = () => { setEditingId(null); setForm(FORM_VUOTO); setShowForm(true); };
  const apriModifica = (b) => {
    setEditingId(b.id);
    setForm({
      modello: b.modello || '', ean: b.ean || '', id_marca: b.id_marca || '', brand: b.brand || '',
      nomeProdotto: b.nomeProdotto || '',
      prezzoPubblico: b.prezzoPubblico || '', prezzoNegozio: b.prezzoNegozio || '',
      arrivoPrevisto: b.arrivoPrevisto || '', specializzati: b.specializzati || '',
      img: b.img || '', _previewImg: b.immagine?.path_file || '',
      schedaTecnicaPdf: b.schedaTecnicaPdf || ''
    });
    setShowForm(true);
  };

  const setF = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    else { setSortBy(col); setSortDir('ASC'); }
    setPage(1);
  };
  const frecciaSort = (col) => (sortBy === col ? (sortDir === 'ASC' ? ' ▲' : ' ▼') : '');

  const onTipoChange = (idTipo) => {
    const tipoSel = tipi.find((t) => String(t.id) === String(idTipo));
    const elettrica = tipoSel ? /elettr/i.test(tipoSel.descrizione) : form.elettrica;
    setF({ id_tipo: idTipo, elettrica });
  };

  const salva = async () => {
    if (!form.modello || !form.modello.trim()) { toast.error('Il modello è obbligatorio'); return; }
    setSalvando(true);
    try {
      // normalizza numerici vuoti a null
      const payload = { ...form };
      delete payload._previewImg;
      ['id_marca', 'prezzoPubblico', 'prezzoNegozio', 'schedaTecnicaPdf'].forEach((k) => { payload[k] = payload[k] === '' ? null : payload[k]; });
      // marca denormalizzata dal select
      const marcaSel = marche.find((m) => String(m.id) === String(form.id_marca));
      if (marcaSel) payload.brand = marcaSel.descrizione;
      // prezzo mostrato in lista = prezzo al pubblico
      payload.prezzo_listino = payload.prezzoPubblico != null ? String(payload.prezzoPubblico) : null;
      if (editingId) {
        await api.put(`/bici/${editingId}`, payload);
        toast.success('Bici aggiornata');
      } else {
        await api.post('/bici', payload);
        toast.success('Bici creata');
      }
      setShowForm(false);
      caricaBici();
    } catch (e) {
      toast.error('Errore salvataggio: ' + (e.response?.data?.error || e.message));
    } finally {
      setSalvando(false);
    }
  };

  // Step 1: scelto il file → chiedi l'anteprima (dry-run, non scrive)
  const onSelectExcel = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setExcelFile(file);
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/bici/import-excel?preview=1', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      console.log('Anteprima import bici:', data);
      setPreviewData(data);
      setImportModalOpen(true);
    } catch (err) {
      toast.error('Errore lettura file: ' + (err.response?.data?.message || err.message));
    } finally {
      setImporting(false);
    }
  };

  // Step 2: conferma → import reale
  const confermaImport = async () => {
    if (!excelFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', excelFile);
      const { data } = await api.post('/bici/import-excel', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.success) {
        toast.success(`Import completato: ${data.creati} create, ${data.aggiornati} aggiornate` +
          (data.saltati ? `, ${data.saltati} righe saltate` : '') +
          (data.errori?.length ? `, ${data.errori.length} errori` : ''));
        setImportModalOpen(false);
        setPreviewData(null);
        setExcelFile(null);
        setPage(1);
        caricaLookup();
        caricaBici();
      }
    } catch (err) {
      toast.error('Errore import: ' + (err.response?.data?.message || err.message));
    } finally {
      setImporting(false);
    }
  };

  const elimina = async (id) => {
    if (!window.confirm('Eliminare questa bici?')) return;
    try {
      await api.delete(`/bici/${id}`);
      toast.success('Bici eliminata');
      caricaBici();
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };

  const onUploadImmagine = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('immagine', file);
      const { data } = await api.post('/bici/immagini/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.immagine && data.immagine.id) {
        setF({ img: String(data.immagine.id), _previewImg: data.immagine.path_file });
        toast.success('Immagine caricata');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Errore upload immagine');
    } finally {
      setUploading(false);
    }
  };

  const onUploadPdf = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Il file deve essere un PDF'); return; }
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const { data } = await api.post('/bici/pdf/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.url) {
        setF({ schedaTecnicaPdf: data.url });
        toast.success('Scheda tecnica caricata');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Errore upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const nuovaMarca = async () => {
    const nome = window.prompt('Nome della nuova marca:');
    if (!nome || !nome.trim()) return;
    try {
      const { data } = await api.post('/bici/marche', { descrizione: nome.trim() });
      await caricaLookup();
      setF({ id_marca: data.id, brand: data.descrizione });
      toast.success('Marca aggiunta');
    } catch (e) {
      toast.error('Errore creazione marca');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BoltIcon className="h-6 w-6 text-emerald-600" /> Gestione Bici
          </h1>
          <p className="text-sm text-gray-500">{total} bici in catalogo</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={excelInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onSelectExcel} />
          <button onClick={() => excelInputRef.current && excelInputRef.current.click()} disabled={importing}
            className="inline-flex items-center gap-1 px-4 py-2 border border-emerald-600 text-emerald-700 text-sm rounded-md hover:bg-emerald-50 disabled:opacity-50">
            <ArrowUpTrayIcon className="h-4 w-4" /> {importing ? 'Import…' : 'Importa Excel'}
          </button>
          <button onClick={apriNuovo}
            className="inline-flex items-center gap-1 px-4 py-2 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700">
            <PlusIcon className="h-4 w-4" /> Nuova bici
          </button>
        </div>
      </div>

      {/* Banner provvigioni */}
      <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-emerald-50/60 border border-emerald-100 rounded-lg text-sm">
        <BanknotesIcon className="h-4 w-4 text-emerald-600 flex-shrink-0" />
        <span className="text-gray-700">
          Provvigione agente: <span className="font-semibold text-emerald-700">{percProvv != null ? `${percProvv}%` : '—'}</span> del margine (Prezzo pubblico − Prezzo negozio)
        </span>
        <Link to="/bike/impostazioni" className="ml-auto inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 font-medium">
          <Cog6ToothIcon className="h-4 w-4" /> Impostazioni
        </Link>
      </div>

      {/* Filtri */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
        <div className="md:col-span-3 relative">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2 top-2.5" />
          <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Cerca modello, brand, nome prodotto o EAN…"
            className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-md text-sm" />
        </div>
        <select value={fMarca} onChange={(e) => { setPage(1); setFMarca(e.target.value); }} className="border border-gray-300 rounded-md text-sm px-2 py-2">
          <option value="">Tutti i brand</option>
          {marche.map((m) => <option key={m.id} value={m.id}>{m.descrizione}</option>)}
        </select>
      </div>

      {/* Tabella */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th onClick={() => toggleSort('modello')} className="px-3 py-2 text-left cursor-pointer select-none hover:text-gray-700 whitespace-nowrap">Modello{frecciaSort('modello')}</th>
              <th onClick={() => toggleSort('ean')} className="px-3 py-2 text-left cursor-pointer select-none hover:text-gray-700 whitespace-nowrap">EAN{frecciaSort('ean')}</th>
              <th onClick={() => toggleSort('brand')} className="px-3 py-2 text-left cursor-pointer select-none hover:text-gray-700 whitespace-nowrap">Brand{frecciaSort('brand')}</th>
              <th onClick={() => toggleSort('nomeProdotto')} className="px-3 py-2 text-left cursor-pointer select-none hover:text-gray-700 whitespace-nowrap">Nome prodotto{frecciaSort('nomeProdotto')}</th>
              <th onClick={() => toggleSort('prezzoPubblico')} className="px-3 py-2 text-right cursor-pointer select-none hover:text-gray-700 whitespace-nowrap">Prezzo pubblico{frecciaSort('prezzoPubblico')}</th>
              <th onClick={() => toggleSort('prezzoNegozio')} className="px-3 py-2 text-right cursor-pointer select-none hover:text-gray-700 whitespace-nowrap">Prezzo negozio (IVA){frecciaSort('prezzoNegozio')}</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Provvigione</th>
              <th onClick={() => toggleSort('arrivoPrevisto')} className="px-3 py-2 text-left cursor-pointer select-none hover:text-gray-700 whitespace-nowrap">Arrivo{frecciaSort('arrivoPrevisto')}</th>
              <th onClick={() => toggleSort('specializzati')} className="px-3 py-2 text-left cursor-pointer select-none hover:text-gray-700 whitespace-nowrap">Specializzati{frecciaSort('specializzati')}</th>
              <th className="px-3 py-2 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400">Caricamento…</td></tr>
            ) : bici.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400">Nessuna bici trovata</td></tr>
            ) : bici.map((b) => (
              <tr key={b.id} className="hover:bg-emerald-50/40 transition-colors">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2.5">
                    <div className="h-11 w-16 bg-gray-50 rounded-md border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {b.immagine?.path_file
                        ? <img src={b.immagine.path_file} alt="" className="h-full w-full object-contain" />
                        : <PhotoIcon className="h-4 w-4 text-gray-300" />}
                    </div>
                    <span className="font-medium text-gray-900">{b.modello || '—'}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap font-mono text-xs">{b.ean || '—'}</td>
                <td className="px-3 py-2 font-medium text-gray-800">{b.marca?.descrizione || b.brand || '—'}</td>
                <td className="px-3 py-2 text-gray-700">{b.nomeProdotto || '—'}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-gray-900">{formatPrezzo(b.prezzoPubblico)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-gray-600">{formatPrezzo(prezzoNegozioConIva(b.prezzoNegozio))}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {provvigioneBici(b) != null
                    ? <span className="font-semibold text-emerald-700">{formatPrezzo(provvigioneBici(b))}</span>
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-2 text-gray-600">{b.arrivoPrevisto || '—'}</td>
                <td className="px-3 py-2 text-gray-600">{b.specializzati || '—'}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => apriModifica(b)} className="p-1 text-blue-600 hover:text-blue-800" title="Modifica"><PencilIcon className="h-4 w-4" /></button>
                    <button onClick={() => elimina(b.id)} className="p-1 text-red-600 hover:text-red-800" title="Elimina"><TrashIcon className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginazione */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4 text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Precedente</button>
          <span>Pagina {page} di {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Successiva</button>
        </div>
      )}

      {/* Form modale */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-3">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b sticky top-0 bg-white">
              <h3 className="font-bold text-gray-900">{editingId ? 'Modifica bici' : 'Nuova bici'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              {/* Immagine */}
              <div className="flex items-center gap-4">
                <div className="relative h-28 w-40 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {uploading ? (
                    <div className="flex flex-col items-center text-emerald-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500"></div>
                      <span className="text-[10px] text-gray-400 mt-1">Caricamento…</span>
                    </div>
                  ) : form._previewImg ? (
                    <>
                      <img src={form._previewImg} alt="Immagine bici" className="h-full w-full object-contain" />
                      <button type="button" onClick={() => setF({ img: '', _previewImg: '' })}
                        className="absolute top-1 right-1 bg-white/90 border border-gray-200 rounded-full p-0.5 text-gray-500 hover:text-red-600 shadow-sm" title="Rimuovi immagine">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <div className="flex flex-col items-center text-gray-300">
                      <PhotoIcon className="h-8 w-8" />
                      <span className="text-[10px] text-gray-400 mt-1">Nessuna immagine</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                    <ArrowUpTrayIcon className="h-4 w-4" />
                    {form._previewImg ? 'Cambia immagine' : 'Carica immagine'}
                    <input type="file" accept="image/*" className="hidden" onChange={onUploadImmagine} />
                  </label>
                  {form._previewImg && <p className="text-xs text-emerald-600 font-medium mt-1.5">✓ Immagine allegata</p>}
                  <p className="text-[11px] text-gray-400 mt-1">JPG o PNG · max 15 MB. Ricordati di salvare.</p>
                </div>
              </div>

              {/* Scheda tecnica (PDF) */}
              <div className="flex items-center gap-4 border-t border-gray-100 pt-4">
                <div className="h-28 w-40 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {uploadingPdf ? (
                    <div className="flex flex-col items-center text-emerald-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500"></div>
                      <span className="text-[10px] text-gray-400 mt-1">Caricamento…</span>
                    </div>
                  ) : form.schedaTecnicaPdf ? (
                    <a href={form.schedaTecnicaPdf} target="_blank" rel="noopener noreferrer"
                      className="flex flex-col items-center text-red-500 hover:text-red-600" title="Apri il PDF">
                      <span className="text-3xl leading-none">📄</span>
                      <span className="text-[10px] text-gray-500 mt-1 underline">Apri PDF</span>
                    </a>
                  ) : (
                    <div className="flex flex-col items-center text-gray-300">
                      <span className="text-3xl leading-none">📄</span>
                      <span className="text-[10px] text-gray-400 mt-1">Nessuna scheda</span>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Scheda tecnica (PDF)</p>
                  <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      {form.schedaTecnicaPdf ? 'Cambia PDF' : 'Carica PDF'}
                      <input type="file" accept="application/pdf" className="hidden" onChange={onUploadPdf} />
                    </label>
                    {form.schedaTecnicaPdf && (
                      <button type="button" onClick={() => setF({ schedaTecnicaPdf: '' })}
                        className="inline-flex items-center gap-1 text-sm px-2.5 py-2 border border-gray-200 rounded-md text-gray-500 hover:text-red-600 hover:border-red-200">
                        <XMarkIcon className="h-4 w-4" /> Rimuovi
                      </button>
                    )}
                  </div>
                  {form.schedaTecnicaPdf && <p className="text-xs text-emerald-600 font-medium mt-1.5">✓ Scheda tecnica allegata</p>}
                  <p className="text-[11px] text-gray-400 mt-1">Solo PDF · max 25 MB. Ricordati di salvare.</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-600">Modello
                  <input value={form.modello} onChange={(e) => setF({ modello: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                </label>
                <label className="text-xs text-gray-600">Codice EAN
                  <input value={form.ean} onChange={(e) => setF({ ean: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                </label>
                <label className="text-xs text-gray-600">Brand
                  <div className="flex gap-1">
                    <select value={form.id_marca} onChange={(e) => setF({ id_marca: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                      <option value="">—</option>
                      {marche.map((m) => <option key={m.id} value={m.id}>{m.descrizione}</option>)}
                    </select>
                    <button type="button" onClick={nuovaMarca} className="px-2 border border-gray-300 rounded text-sm" title="Nuovo brand">+</button>
                  </div>
                </label>
                <label className="text-xs text-gray-600">Nome prodotto
                  <input value={form.nomeProdotto} onChange={(e) => setF({ nomeProdotto: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                </label>
                <label className="text-xs text-gray-600">Prezzo al pubblico (con IVA) €
                  <input type="number" step="0.01" value={form.prezzoPubblico} onChange={(e) => setF({ prezzoPubblico: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                </label>
                <label className="text-xs text-gray-600">Prezzo al negozio (no IVA) €
                  <input type="number" step="0.01" value={form.prezzoNegozio} onChange={(e) => setF({ prezzoNegozio: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                </label>
                <label className="text-xs text-gray-600">Arrivo Previsto
                  <input value={form.arrivoPrevisto} onChange={(e) => setF({ arrivoPrevisto: e.target.value })} placeholder="Es. Settembre 2026 / Disponibile" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                </label>
                <label className="text-xs text-gray-600">Specializzati
                  <input value={form.specializzati} onChange={(e) => setF({ specializzati: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t sticky bottom-0 bg-white">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Annulla</button>
              <button onClick={salva} disabled={salvando} className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50">
                {salvando ? 'Salvataggio…' : (editingId ? 'Aggiorna' : 'Crea bici')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale anteprima import Excel */}
      {importModalOpen && previewData && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-3">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-bold text-gray-900">Anteprima import Excel</h3>
              <button onClick={() => { setImportModalOpen(false); setPreviewData(null); setExcelFile(null); }} className="p-1 text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto">
              {(previewData.colonneRiconosciute?.length || 0) === 0 ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
                  <p className="font-semibold text-red-700 mb-2">Nessuna colonna riconosciuta nel file.</p>
                  <p className="text-gray-600">Fogli nel file: {(previewData.diagnostica?.fogli || []).join(', ') || '—'}</p>
                  <p className="text-gray-600 mt-2">Prime righe trovate:</p>
                  <pre className="text-xs bg-white border rounded p-2 mt-1 overflow-x-auto">{JSON.stringify(previewData.diagnostica?.primeRighe || [], null, 1)}</pre>
                  <p className="text-gray-500 mt-2">Le colonne attese sono: <b>Modello, Codice EAN, Brand, Nome prodotto, Prezzo al pubblico (con IVA), Prezzo al negozio (no iva), Arrivo Previsto, Specializzati</b>.</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 mb-3 text-xs text-gray-600">
                    <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded">Foglio: {previewData.foglio}</span>
                    <span className="px-2 py-1 bg-gray-100 rounded">Intestazione: riga {previewData.rigaIntestazione}</span>
                    <span className="px-2 py-1 bg-gray-100 rounded">{previewData.totaleRighe} bici da importare</span>
                    {previewData.saltati > 0 && <span className="px-2 py-1 bg-gray-100 rounded">{previewData.saltati} righe vuote saltate</span>}
                    <span className={`px-2 py-1 rounded ${previewData.colonneRiconosciute.length >= 8 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>Colonne riconosciute: {previewData.colonneRiconosciute.length}/8</span>
                  </div>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 text-gray-500">
                        <tr>
                          <th className="px-2 py-2 text-left">Modello</th>
                          <th className="px-2 py-2 text-left">EAN</th>
                          <th className="px-2 py-2 text-left">Brand</th>
                          <th className="px-2 py-2 text-left">Nome prodotto</th>
                          <th className="px-2 py-2 text-right">Prezzo pubblico</th>
                          <th className="px-2 py-2 text-right">Prezzo negozio</th>
                          <th className="px-2 py-2 text-left">Arrivo</th>
                          <th className="px-2 py-2 text-left">Specializzati</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(previewData.anteprima || []).map((r, i) => (
                          <tr key={i}>
                            <td className="px-2 py-1.5">{r.modello || '—'}</td>
                            <td className="px-2 py-1.5">{r.ean || '—'}</td>
                            <td className="px-2 py-1.5">{r.brand || '—'}</td>
                            <td className="px-2 py-1.5">{r.nomeProdotto || '—'}</td>
                            <td className="px-2 py-1.5 text-right">{r.prezzoPubblico != null ? '€' + r.prezzoPubblico : '—'}</td>
                            <td className="px-2 py-1.5 text-right">{r.prezzoNegozio != null ? '€' + r.prezzoNegozio : '—'}</td>
                            <td className="px-2 py-1.5">{r.arrivoPrevisto || '—'}</td>
                            <td className="px-2 py-1.5">{r.specializzati || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Anteprima delle prime {(previewData.anteprima || []).length} righe. Controlla che i dati siano nelle colonne giuste, poi conferma.</p>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t">
              <button onClick={() => { setImportModalOpen(false); setPreviewData(null); setExcelFile(null); }} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Annulla</button>
              {(previewData.colonneRiconosciute?.length || 0) > 0 && (
                <button onClick={confermaImport} disabled={importing} className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50">
                  {importing ? 'Import…' : `Conferma e importa ${previewData.totaleRighe} bici`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
